import { useTranslation } from 'react-i18next';

import { useIsCoinsCurrency } from '@/feature/betslip-sbk/hooks/use-coins-currency';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { TotalStakeError, TotalStakeErrors } from '@/feature/betslip-sbk/types';
import { StakeInputErrors } from '@/feature/betslip-sbk/types';
import { totalStakeValidators } from '@/feature/betslip-sbk/utils/validators';
import { coinsTotalStakeValidators } from '@/feature/betslip-sbk/utils/validators-coins';
import { useShallow } from 'zustand/react/shallow';

import { WarningBanner } from './WarningBanner';

const isCoinsError = (error: string): error is keyof typeof coinsTotalStakeValidators => {
    return error in coinsTotalStakeValidators;
};

const isSbkError = (error: string): error is keyof typeof totalStakeValidators => {
    return error in totalStakeValidators;
};

const RG_ERRORS: TotalStakeError[] = [
    TotalStakeErrors.ExceedDailyWagerLimit,
    TotalStakeErrors.ExceedWeeklyWagerLimit,
    TotalStakeErrors.ExceedMonthlyWagerLimit,
];

export const TotalStakeWarnings = ({ showOnlyRgErrors = false }: { showOnlyRgErrors?: boolean }) => {
    const { t } = useTranslation(['betslip_sbk', 'sweeps', 'common']);
    const totalStakeErrors = useSbkBetSlipStore(
        useShallow(state =>
            showOnlyRgErrors
                ? state.totalStakeErrors.filter(error => RG_ERRORS.includes(error))
                : state.totalStakeErrors
        )
    );

    const totalBonusBetsExceedStakeErrors = useSbkBetSlipStore(state => {
        return Object.values(state.bets).filter(
            bet =>
                state.isBetrBucksEnabled[bet.id] &&
                !!bet.stake &&
                bet.stakeInputError === StakeInputErrors.ExceedSingleWalletBalance
        ).length;
    });

    const totalBetsExceedStakeErrors = useSbkBetSlipStore(state => {
        return Object.values(state.bets).filter(
            bet =>
                !state.isBetrBucksEnabled[bet.id] &&
                !!bet.stake &&
                bet.stakeInputError === StakeInputErrors.ExceedSingleWalletBalance
        ).length;
    });

    const isCoinsCurrency = useIsCoinsCurrency();

    if (totalStakeErrors.length === 0) {
        return null;
    }

    const warnings: string[] = [];

    for (const error of totalStakeErrors) {
        if (isCoinsCurrency && isCoinsError(error)) {
            const key = coinsTotalStakeValidators[error].translationKey;
            switch (error) {
                case TotalStakeErrors.ExceedWalletCashBalance:
                    warnings.push(t(key, { currency: t('sweeps:gold_coins'), count: totalBetsExceedStakeErrors }));
                    break;
            }
        } else if (!isCoinsCurrency && isSbkError(error)) {
            const key = totalStakeValidators[error].translationKey;
            switch (error) {
                case TotalStakeErrors.ExceedWalletCashBalance:
                    warnings.push(t(key, { currency: t('sweeps:currency_cash'), count: totalBetsExceedStakeErrors }));
                    break;
                case TotalStakeErrors.ExceedWalletBetrBucksBalance:
                    warnings.push(t(key, { currency: t('common:bonus'), count: totalBonusBetsExceedStakeErrors }));
                    break;
                case TotalStakeErrors.ExceedDailyWagerLimit:
                    warnings.push(t(key, { limit_type: t('common:daily') }));
                    break;
                case TotalStakeErrors.ExceedWeeklyWagerLimit:
                    warnings.push(t(key, { limit_type: t('common:weekly') }));
                    break;
                case TotalStakeErrors.ExceedMonthlyWagerLimit:
                    warnings.push(t(key, { limit_type: t('common:monthly') }));
                    break;
            }
        }
    }

    return (
        <>
            {warnings.map((message, index) => (
                <WarningBanner key={index} message={message} />
            ))}
        </>
    );
};
